/**
 * Enrolled tablets and staff PINs (#80, D6).
 *
 * A shared tablet behind a bar cannot run on personal passwords — that ends
 * as one password the whole team knows, which still works for whoever left
 * last month. D6's answer, built here:
 *
 *   - **The tablet is the real gate.** A manager enrols it for one venue
 *     while signed in properly; BarOS issues it a long random key, kept on
 *     the tablet and only as a hash on our side. Revoking it is one click and
 *     takes effect on the next request.
 *   - **A PIN says who is holding it.** Four to six digits, set by each
 *     person from their own sign-in, never seen by anybody else. Unlocking
 *     opens a session in that person's name, so every change is theirs.
 *   - **A PIN alone is weak, so it is fenced.** Obvious PINs are refused;
 *     five wrong guesses lock that person's PIN; twenty on one tablet in an
 *     hour lock the tablet; and the actions an organisation chooses (big
 *     orders, cash-up, prices, roles, big wastage) still ask for a full
 *     password sign-in.
 *   - **The tablet locks itself** after the organisation's idle time, and
 *     each person's place is kept for them: unlocking takes them back to
 *     the screen they were on when they last locked this tablet.
 *
 * Pure rules and shapes only; hashing, storage and the routes are in
 * packages/api.
 */
// ------------------------------------------------------------------- PINs
export const PIN_MIN = 4;
export const PIN_MAX = 6;
/**
 * Why a PIN will not do, or null when it will. Refuses the guesses anyone
 * tries first: one digit repeated, a straight run up or down, and a pair
 * repeated. Four digits is ten thousand PINs; the point is to stop the
 * dozen everyone starts with.
 */
export function pinProblem(pin) {
    if (typeof pin !== "string" || !/^\d+$/.test(pin))
        return "A PIN is digits only";
    if (pin.length < PIN_MIN || pin.length > PIN_MAX)
        return `A PIN is ${PIN_MIN} to ${PIN_MAX} digits`;
    const digits = [...pin].map(Number);
    if (digits.every((d) => d === digits[0]))
        return "Pick a PIN that is not one digit repeated";
    const steps = digits.slice(1).map((d, i) => d - digits[i]);
    if (steps.every((s) => s === 1) || steps.every((s) => s === -1))
        return "Pick a PIN that is not a run like 1234";
    if (pin.length % 2 === 0 && pin === pin.slice(0, 2).repeat(pin.length / 2))
        return "Pick a PIN that is not a pair repeated";
    return null;
}
/** Wrong PINs before that person's PIN locks. */
export const PIN_ATTEMPTS = 5;
export const PIN_LOCK_MINUTES = 15;
/** Wrong PINs on one tablet within the window before the tablet locks. */
export const DEVICE_ATTEMPTS = 20;
export const DEVICE_WINDOW_MINUTES = 60;
export const DEVICE_LOCK_MINUTES = 30;
/** After a wrong PIN: the record's new failure count and any lock. */
export function afterWrongPin(record, now) {
    const failures = record.failures + 1;
    return failures >= PIN_ATTEMPTS
        ? { failures: 0, lockedUntil: new Date(now.getTime() + PIN_LOCK_MINUTES * 60_000).toISOString() }
        : { failures, lockedUntil: null };
}
/** After a wrong PIN on a tablet: its rolling failure count and any lock. */
export function afterDeviceFailure(device, now) {
    const since = device.failuresSince ? Date.parse(device.failuresSince) : 0;
    const fresh = now.getTime() - since > DEVICE_WINDOW_MINUTES * 60_000;
    const failures = (fresh ? 0 : device.failures) + 1;
    const failuresSince = fresh ? now.toISOString() : (device.failuresSince ?? now.toISOString());
    return failures >= DEVICE_ATTEMPTS
        ? { failures: 0, failuresSince: now.toISOString(), lockedUntil: new Date(now.getTime() + DEVICE_LOCK_MINUTES * 60_000).toISOString() }
        : { failures, failuresSince, lockedUntil: null };
}
/** Whether a lock is still in force at `now`. */
export function locked(lockedUntil, now) {
    return Boolean(lockedUntil) && Date.parse(lockedUntil) > now.getTime();
}
/** The longest an unlock lasts, whatever the idle setting. */
export const SESSION_HOURS = 14;
/** Whether a session may still act at `now`. */
export function sessionLive(s, now) {
    if (Date.parse(s.expiresAt) <= now.getTime())
        return false;
    if (s.idleMinutes === null)
        return true;
    return now.getTime() - Date.parse(s.lastUsedAt) <= s.idleMinutes * 60_000;
}
/**
 * The two halves an `Authorization: Device <device key> <session>` header
 * carries, each `<id>.<secret>`; or null when the header is not that.
 * The session is optional: the lock screen speaks for the tablet alone.
 */
export function parseDeviceAuthorization(header) {
    if (!header)
        return null;
    const parts = header.trim().split(/\s+/);
    if (parts[0]?.toLowerCase() !== "device" || parts.length < 2 || parts.length > 3)
        return null;
    const pair = (token) => {
        const m = token ? /^([\w-]{8,64})\.([\w-]{20,200})$/.exec(token) : null;
        return m ? { id: m[1], secret: m[2] } : null;
    };
    const device = pair(parts[1]);
    if (!device)
        return null;
    if (parts.length === 2)
        return { device, session: null };
    const session = pair(parts[2]);
    return session ? { device, session } : null;
}
/**
 * Where a person was when they locked a tablet, as the address bar had it.
 * Only a path within the app: anything else is dropped rather than stored,
 * so the value can never send somebody elsewhere.
 */
export function resumePath(url) {
    if (typeof url !== "string" || url.length > 500)
        return null;
    if (!url.startsWith("/") || url.startsWith("//") || /[\s\\]/.test(url))
        return null;
    return url;
}
export const IDLE_CHOICES = [3, 5, 10, 15, 30, 60, null];
export const DEFAULT_TABLET_POLICY = {
    idleMinutes: 10,
    stepUp: {
        orders: { on: true, overCents: 50_000 },
        wastage: { on: false, overCents: 50_000 },
        cashupFinalise: false,
        prices: true,
        roles: true,
    },
};
const isRecord = (v) => typeof v === "object" && v !== null && !Array.isArray(v);
const bool = (v, fallback) => (typeof v === "boolean" ? v : fallback);
const cents = (v, fallback) => typeof v === "number" && Number.isInteger(v) && v >= 0 && v <= 100_000_000 ? v : fallback;
/**
 * The policy as stored, with every missing or malformed part settled to its
 * default — one rule for every reader, so a half-written setting cannot make
 * one route stricter than another.
 */
export function settledTabletPolicy(raw) {
    const d = DEFAULT_TABLET_POLICY;
    if (!isRecord(raw))
        return d;
    const idle = raw["idleMinutes"];
    const s = isRecord(raw["stepUp"]) ? raw["stepUp"] : {};
    const sub = (k) => (isRecord(s[k]) ? s[k] : {});
    return {
        idleMinutes: idle === null ? null : IDLE_CHOICES.includes(idle) ? idle : d.idleMinutes,
        stepUp: {
            orders: { on: bool(sub("orders")["on"], d.stepUp.orders.on), overCents: cents(sub("orders")["overCents"], d.stepUp.orders.overCents) },
            wastage: { on: bool(sub("wastage")["on"], d.stepUp.wastage.on), overCents: cents(sub("wastage")["overCents"], d.stepUp.wastage.overCents) },
            cashupFinalise: bool(s["cashupFinalise"], d.stepUp.cashupFinalise),
            prices: bool(s["prices"], d.stepUp.prices),
            roles: bool(s["roles"], d.stepUp.roles),
        },
    };
}
/** A policy from a settings save, or why not. Strict where `settledTabletPolicy` is forgiving. */
export function validateTabletPolicy(v) {
    if (!isRecord(v))
        return { ok: false, error: "tabletPolicy must be an object" };
    if (!IDLE_CHOICES.includes(v["idleMinutes"])) {
        return { ok: false, error: `tabletPolicy.idleMinutes must be one of ${IDLE_CHOICES.map(String).join(", ")}` };
    }
    const settled = settledTabletPolicy(v);
    return { ok: true, value: settled };
}
/** Whether `action`, worth `amountCents` where that matters, needs a full sign-in under `policy`. */
export function needsStepUp(policy, action, amountCents = 0) {
    switch (action) {
        case "orders":
            return policy.stepUp.orders.on && amountCents > policy.stepUp.orders.overCents;
        case "wastage":
            return policy.stepUp.wastage.on && amountCents > policy.stepUp.wastage.overCents;
        default:
            return policy.stepUp[action];
    }
}
/** The header a step-up refusal carries, so the app knows to ask for a password. */
export const STEP_UP_HEADER = "x-baros-step-up";
//# sourceMappingURL=device.js.map